import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { collection, doc, getDocs, getFirestore, orderBy, query, setDoc, Timestamp } from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import { app } from '../../firebase'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import swal from 'sweetalert'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate, faChalkboardUser, faMagnifyingGlass, faPlus, faVideo } from '@fortawesome/free-solid-svg-icons'
import profileFallback from '../../Images/place.jpg'

const formatCount = (value) => Number(value || 0).toLocaleString('en-KE')

function DarasaComp() {
  const db = getFirestore(app)
  const auth = getAuth()
  const [modalShow, setModalShow] = useState(false)
  const [instructors, setInstructors] = useState([])
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const intakeDateRef = useRef()
  const classDescRef = useRef()
  const startDateRef = useRef()
  const startTimeRef = useRef()
  const endTimeRef = useRef()
  const selectInstructorRef = useRef()

  const fetchInstructors = useCallback(async () => {
    const querySnapshot = await getDocs(collection(db, 'Employees'))
    const instructorsList = []

    querySnapshot.forEach((item) => {
      instructorsList.push({ id: item.id, ...item.data() })
    })

    setInstructors(instructorsList)
  }, [db])

  const fetchClasses = useCallback(async (options = {}) => {
    const initialLoad = options.initial === true

    if (initialLoad) {
      setLoading(true)
    } else {
      setRefreshing(true)
    }

    try {
      const classesQuery = query(collection(db, 'allclasses'), orderBy('timeCreated', 'desc'))
      const querySnapshot = await getDocs(classesQuery)
      const classList = []

      querySnapshot.forEach((item) => {
        classList.push({ id: item.id, ...item.data() })
      })

      setClasses(classList)
    } catch (error) {
      console.error('Failed to load classes:', error)
      setClasses([])
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [db])

  useEffect(() => {
    let cancelled = false

    const loadPageData = async () => {
      await Promise.all([
        fetchInstructors(),
        fetchClasses({ initial: true })
      ])
    }

    loadPageData().catch((error) => {
      if (!cancelled) {
        console.error('Failed to load classes page:', error)
      }
    })

    return () => {
      cancelled = true
    }
  }, [fetchClasses, fetchInstructors])

  const handleAddClass = async () => {
    const intakeDate = intakeDateRef.current.value
    const classDesc = classDescRef.current.value
    const startDate = startDateRef.current.value
    const startTime = startTimeRef.current.value
    const endTime = endTimeRef.current.value
    const selectInstructor = selectInstructorRef.current.value
    const myUserId = auth.currentUser.uid
    const timeStamp = Timestamp.now()

    document.getElementById('submitBtn').style.display = 'none'
    document.getElementById('spinnerBtn').style.display = 'inline-block'

    try {
      const response = await fetch('https://api.daily.co/v1/rooms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer 9ca6078bdb4b21b14f89f74bf52454758a58e2488b6ce5a04e640014f9fc5c7c'
        },
        body: JSON.stringify({ properties: {} })
      })

      if (response.ok) {
        const data = await response.json()
        const roomUrl = data.name
        const classDocRef = doc(db, 'allclasses', roomUrl)

        await setDoc(classDocRef, {
          intakeDate,
          classDesc,
          startDate,
          isActive: 'Active',
          createdBy: myUserId,
          timeCreated: timeStamp,
          startTime,
          endTime,
          roomID: roomUrl,
          instructor: selectInstructor
        })

        swal('Success', 'Class created successfully!', 'success')
        setModalShow(false)
        await fetchClasses()
      } else {
        console.error('Error creating room: ', response.statusText)
        swal('Error', 'There was an error creating the room.', 'error')
      }
    } catch (error) {
      console.error('Error during fetch: ', error)
      swal('Error', 'Error during the room creation process.', 'error')
    } finally {
      document.getElementById('submitBtn').style.display = 'inline-block'
      document.getElementById('spinnerBtn').style.display = 'none'
    }
  }

  const filteredClasses = useMemo(() => {
    const search = searchTerm.trim().toLowerCase()

    if (!search) {
      return classes
    }

    return classes.filter((classItem) => {
      const instructorName = instructors.find((item) => item.id === classItem.instructor)?.employeeName || 'Unknown'
      const haystack = [
        classItem.classDesc,
        classItem.isActive,
        classItem.startTime,
        classItem.endTime,
        classItem.intakeDate,
        classItem.startDate,
        instructorName,
        classItem.roomID
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return haystack.includes(search)
    })
  }, [classes, instructors, searchTerm])

  const classStats = useMemo(() => {
    const active = classes.filter((item) => String(item.isActive || '').toLowerCase().includes('active')).length
    const inactive = classes.length - active
    const instructorsUsed = new Set(classes.map((item) => item.instructor).filter(Boolean)).size

    return {
      total: classes.length,
      active,
      inactive,
      instructorsUsed
    }
  }, [classes])

  const getInstructorInfo = useCallback((classItem) => {
    const instructor = instructors.find((item) => item.id === classItem.instructor)

    return {
      name: instructor?.employeeName || 'Unknown instructor',
      profilePic: instructor?.profilePic || profileFallback
    }
  }, [instructors])

  const hasClasses = filteredClasses.length > 0

  return (
    <div className="clientsComp darasaComp darasaPage">
      <section className="appsHeader darasaHeader">
        <div className="attendanceHeaderCopy">
          <p className="appsKicker">Academic Setup</p>
          <h4>Classes</h4>
          <p className="appsSubtext">
            Create, review, and manage class rooms in one place. Search quickly, check status at a glance, and jump
            straight into a class when you need to review students.
          </p>
        </div>

        <div className="appsHeaderStats">
          <div className="appsStatChip">
            <span>Total classes</span>
            <strong>{formatCount(classStats.total)}</strong>
          </div>
          <div className="appsStatChip">
            <span>Active</span>
            <strong>{formatCount(classStats.active)}</strong>
          </div>
          <div className="appsStatChip">
            <span>Instructors used</span>
            <strong>{formatCount(classStats.instructorsUsed)}</strong>
          </div>
          <div className="appsStatChip">
            <span>Inactive</span>
            <strong>{formatCount(classStats.inactive)}</strong>
          </div>
        </div>
      </section>

      <section className="appsFiltersCard darasaFiltersCard">
        <div className="appsSearch input-group darasaSearch">
          <span className="input-group-text">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </span>
          <Form.Control
            type="search"
            placeholder="Search class, instructor, date, or status..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>

        <div className="darasaMeta">
          <span>{formatCount(filteredClasses.length)} classes visible</span>
          <span>{loading ? 'Loading class list...' : 'Live class registry'}</span>
        </div>

        <Button variant="outline-dark" onClick={() => fetchClasses()} disabled={loading || refreshing}>
          <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>

        <Button className="darasaCreateBtn" onClick={() => setModalShow(true)}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Create a class
        </Button>
      </section>

      <section className="darasaSummaryGrid">
        <div className="darasaSummaryCard">
          <span>Active classes</span>
          <strong>{formatCount(classStats.active)}</strong>
          <p>Classes currently marked active in the registry.</p>
        </div>
        <div className="darasaSummaryCard">
          <span>Latest total</span>
          <strong>{formatCount(classStats.total)}</strong>
          <p>All classes currently stored in Firestore.</p>
        </div>
        <div className="darasaSummaryCard">
          <span>Instructor coverage</span>
          <strong>{formatCount(classStats.instructorsUsed)}</strong>
          <p>Unique instructors assigned across the classes.</p>
        </div>
      </section>

      <section className="darasaGrid">
        {loading ? (
          <div className="incomeStateCard darasaStateCard">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading classes...</span>
            </Spinner>
            <p>Loading classes...</p>
          </div>
        ) : hasClasses ? (
          filteredClasses.map((classItem) => {
            const instructorInfo = getInstructorInfo(classItem)

            return (
              <article className="teamsItem2 darasaCard" key={classItem.id}>
                <div className="teamsItemProfilecont2">
                  <div className="roomTopdesc">
                    <div className="defaultAvatarCont">
                      <img className="defaultAvatar" src={instructorInfo.profilePic} alt={instructorInfo.name} />
                    </div>

                    <div className="roomTopContent">
                      <p className="darasaTitle">{classItem.classDesc || 'Class'}</p>
                      <p className="darasaInstructor">
                        <FontAwesomeIcon icon={faVideo} />
                        <span>{instructorInfo.name}</span>
                      </p>
                    </div>
                  </div>

                  <div className="roomTimeClasses">
                    <div>
                      <p className="roomTimeClassesH">Cohort Date</p>
                      <p className="roomTimeClassesP">{classItem.intakeDate || '—'}</p>
                    </div>
                    <div>
                      <p className="roomTimeClassesH">Start Time</p>
                      <p className="roomTimeClassesP">{classItem.startTime || '—'}</p>
                    </div>
                    <div>
                      <p className="roomTimeClassesH2">End Time</p>
                      <p className="roomTimeClassesP2">{classItem.endTime || '—'}</p>
                    </div>
                  </div>

                  <div className="darasaCardActionWrap">
                    <a href={`/view-classes?${classItem.roomID}`} className="joinMyClass">
                      View Class
                    </a>
                  </div>
                </div>
              </article>
            )
          })
        ) : (
          <div className="incomeStateCard darasaStateCard">
            <FontAwesomeIcon icon={faChalkboardUser} className="mb-2" />
            <p>{searchTerm ? 'No classes match the current search.' : 'No classes available yet.'}</p>
          </div>
        )}
      </section>

      <Modal show={modalShow} onHide={() => setModalShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create Class</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Intake Date</Form.Label>
            <Form.Control type="date" ref={intakeDateRef} required />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Pathway</Form.Label>
            <Form.Select ref={classDescRef} required>
              <option value="Product Design">Product Design</option>
              <option value="Software Development">Software Development</option>
              <option value="Machine Learning">Machine Learning</option>
              <option value="Blockchain">Blockchain</option>
              <option value="Data Science">Data Science</option>
              <option value="Robotics">Robotics</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Start Date</Form.Label>
            <Form.Control type="date" ref={startDateRef} required />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Class Start Time</Form.Label>
            <Form.Control type="time" ref={startTimeRef} required />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Class End Time</Form.Label>
            <Form.Control type="time" ref={endTimeRef} required />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Select Instructor</Form.Label>
            <Form.Select ref={selectInstructorRef} required>
              <option value="">Select Instructor</option>
              {instructors.map((instructor) => (
                <option key={instructor.id} value={instructor.id}>
                  {instructor.employeeName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setModalShow(false)}>Close</Button>
          <Button variant="primary" id="submitBtn" onClick={handleAddClass}>Submit</Button>
          <Button variant="primary" disabled id="spinnerBtn" style={{ display: 'none' }}>
            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
            <span> Submitting...</span>
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default DarasaComp
